import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { computed, shallowReactive } from 'vue';
import type { MsalConfig } from '@/config/spaConfig';
import { currentPath, decodeAuthState, encodeAuthState } from '@/auth/returnTo';

/** B2C error returned when the user cancels a user flow (e.g. change password). */
const B2C_USER_CANCELLED = 'AADB2C90091';

const state = shallowReactive({
  account: null as AccountInfo | null,
});

/** Reactive auth flag, safe to use in templates, guards and computed values. */
export const isAuthenticated = computed(() => state.account !== null);

let pca: PublicClientApplication | null = null;
let options: MsalConfig | null = null;
let scopes: string[] = [];
let interactionStarted = false;

function client(): PublicClientApplication {
  if (!pca) {
    throw new Error('authService.initialize() must be awaited before use');
  }
  return pca;
}

function redirectUri(): string {
  return window.location.origin;
}

function policyOf(authority: string): string {
  return authority.split('/').filter(Boolean).pop()?.toLowerCase() ?? '';
}

function flowOf(account: AccountInfo): string | undefined {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  return ((claims?.tfp ?? claims?.acr) as string | undefined)?.toLowerCase();
}

function isSignInAccount(account: AccountInfo): boolean {
  return !!options && flowOf(account) === policyOf(options.loginAuthority);
}

/** Same B2C user across user-flow accounts (object id / local account id). */
function sameUser(a: AccountInfo, b: AccountInfo): boolean {
  return a.localAccountId === b.localAccountId && a.tenantId === b.tenantId;
}

/**
 * B2C creates one cached account per user flow (sign-in, change password, ...),
 * and a shared browser may hold accounts of several users. API tokens must be
 * requested with the CURRENT user's sign-in flow account:
 *  1. the account just returned by a redirect (its sign-in sibling after a change-password),
 *  2. otherwise MSAL's persisted active account,
 *  3. otherwise the first sign-in flow account.
 */
function pickAccount(redirectAccount: AccountInfo | null, active: AccountInfo | null, all: AccountInfo[]): AccountInfo | null {
  const preferred = redirectAccount ?? active;
  if (preferred) {
    if (isSignInAccount(preferred)) return preferred;
    const sibling = all.find((a) => isSignInAccount(a) && sameUser(a, preferred));
    return sibling ?? preferred;
  }
  return all.find(isSignInAccount) ?? all[0] ?? null;
}

function setAccount(account: AccountInfo | null): void {
  client().setActiveAccount(account);
  state.account = account;
}

/** Runs a redirect interaction at most once; resets the guard if starting it fails. */
async function startInteraction(run: () => Promise<void>): Promise<void> {
  if (interactionStarted) return;
  interactionStarted = true;
  try {
    await run();
  } catch (error) {
    interactionStarted = false;
    throw error;
  }
}

/**
 * B2C comes back from logout with ?state=... in the query, which MSAL leaves in the
 * address bar. No portal link uses a 'state' parameter, so it is removed.
 */
function dropLogoutState(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('state')) return;
  url.searchParams.delete('state');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

export interface InitializeResult {
  /** Path to navigate to after a completed B2C redirect, if any. */
  returnTo: string | null;
}

/**
 * Must be awaited BEFORE the router/app are mounted: it consumes the B2C redirect
 * response (URL hash) and restores any cached session.
 */
async function initialize(config: MsalConfig): Promise<InitializeResult> {
  options = config;
  scopes = [config.apiReadScope];
  pca = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.loginAuthority,
      knownAuthorities: [config.knownAuthority],
      redirectUri: redirectUri(),
      postLogoutRedirectUri: redirectUri(),
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  });

  await pca.initialize();

  let result: AuthenticationResult | null = null;
  try {
    // We route explicitly from our own `state` instead of letting MSAL navigate.
    result = await pca.handleRedirectPromise({ navigateToLoginRequestUrl: false });
  } catch (error) {
    const message = (error as Error)?.message ?? '';
    if (!message.includes(B2C_USER_CANCELLED)) {
      console.warn('B2C redirect handling failed', error);
    }
  }

  setAccount(pickAccount(result?.account ?? null, pca.getActiveAccount(), pca.getAllAccounts()));
  dropLogoutState();

  return { returnTo: decodeAuthState(result?.state) };
}

interface InteractiveParams {
  /** Path to come back to after B2C; defaults to the current page. */
  returnTo?: string;
  /** UI language for the B2C pages (ui_locales). */
  locale?: string;
  loginHint?: string;
}

async function login(params: InteractiveParams = {}): Promise<void> {
  await startInteraction(() =>
    client().loginRedirect({
      scopes,
      redirectUri: redirectUri(),
      state: encodeAuthState(params.returnTo ?? currentPath()),
      loginHint: params.loginHint || undefined,
      extraQueryParameters: params.locale ? { ui_locales: params.locale } : undefined,
    }),
  );
}

async function changePassword(params: InteractiveParams = {}): Promise<void> {
  if (!options) return;
  const authority = options.changePasswordAuthority;
  await startInteraction(() =>
    client().loginRedirect({
      authority,
      scopes,
      redirectUri: redirectUri(),
      state: encodeAuthState(params.returnTo ?? currentPath()),
      extraQueryParameters: params.locale ? { ui_locales: params.locale } : undefined,
    }),
  );
}

async function logout(): Promise<void> {
  const account = state.account;
  // The account stays set until the browser leaves: clearing it first re-renders the
  // page as logged out for a moment. MSAL clears its own cache before redirecting.
  await startInteraction(async () => {
    await client().logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: redirectUri(),
    });
  });
}

/** Thrown when a logged-in user's request cannot get a token (never sent unauthenticated). */
export class TokenUnavailableError extends Error {
  constructor(readonly redirecting: boolean, cause?: unknown) {
    super(redirecting ? 'Re-authentication in progress' : 'Access token unavailable', { cause });
    this.name = 'TokenUnavailableError';
  }
}

/**
 * Returns an access token for the Clients API, or null when there is no session
 * (anonymous pages). Throws TokenUnavailableError when there IS a session but no
 * token can be obtained.
 * Never opens popups (blocked on most mobile browsers, where QR links are opened):
 * when interaction is required the page is redirected to B2C and comes back here.
 */
async function acquireToken(): Promise<string | null> {
  const account = state.account;
  if (!account) {
    return null;
  }
  if (interactionStarted) {
    throw new TokenUnavailableError(true);
  }
  try {
    const response = await client().acquireTokenSilent({ scopes, account });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await startInteraction(() =>
        client().acquireTokenRedirect({
          scopes,
          account,
          redirectUri: redirectUri(),
          state: encodeAuthState(currentPath()),
        }),
      );
      throw new TokenUnavailableError(true, error);
    }
    throw new TokenUnavailableError(false, error);
  }
}

export const authService = {
  initialize,
  login,
  logout,
  changePassword,
  acquireToken,
  get isAuthenticated(): boolean {
    return isAuthenticated.value;
  },
};
